/**
 * SlideOrganizer
 *
 * Drag-to-customize slide preview grid. Users can drag play thumbnails
 * between slides to reorganize the export layout. Empty slides auto-collapse.
 * Uses native HTML5 drag-and-drop following the codebase's pattern of
 * avoiding external DnD libraries.
 */

import { useState, useMemo, useCallback } from 'react'
import { CanvasThumbnail } from '@/components/canvas-renderer'
import type { ExportSlide } from '@/types/SlideExport.types'
import type { ExportSection as Section } from '@/components/playbook-editor/Types'

interface SlideOrganizerProps {
	slides: ExportSlide[]
	sections: Section[]
	selectedSectionIds: Set<string>
	isCustomized: boolean
	onMovePlay: (blockId: string, fromSlideIndex: number, toSlideIndex: number) => void
	onReset: () => void
}

export function SlideOrganizer({
	slides,
	sections,
	selectedSectionIds,
	isCustomized,
	onMovePlay,
	onReset,
}: SlideOrganizerProps) {
	const [dragState, setDragState] = useState<DragState | null>(null)
	const [dropTarget, setDropTarget] = useState<number | null>(null)

	// Build a lookup of play names by block ID
	const playNameById = useMemo(() => {
		const map = new Map<string, string>()
		for (const section of sections) {
			if (!selectedSectionIds.has(section.id)) continue
			for (const play of section.plays) {
				map.set(play.id, play.name)
			}
		}
		return map
	}, [sections, selectedSectionIds])

	const handleDragStart = useCallback((blockId: string, slideIndex: number) => {
		setDragState({ blockId, fromSlideIndex: slideIndex })
	}, [])

	const handleDragOver = useCallback((e: React.DragEvent, slideIndex: number) => {
		e.preventDefault()
		e.dataTransfer.dropEffect = 'move'
		setDropTarget(slideIndex)
	}, [])

	const handleDragLeave = useCallback(() => {
		setDropTarget(null)
	}, [])

	const handleDrop = useCallback((e: React.DragEvent, toSlideIndex: number) => {
		e.preventDefault()
		setDropTarget(null)

		if (dragState && dragState.fromSlideIndex !== toSlideIndex) {
			onMovePlay(dragState.blockId, dragState.fromSlideIndex, toSlideIndex)
		}
		setDragState(null)
	}, [dragState, onMovePlay])

	const handleDragEnd = useCallback(() => {
		setDragState(null)
		setDropTarget(null)
	}, [])

	const contentSlideCount = slides.filter((s) => s.type === 'content').length

	return (
		<div className="slide-organizer">
			<div className="slide-organizer__header">
				<span className="slide-organizer__label">
					Slides ({contentSlideCount})
				</span>
				{isCustomized && (
					<button
						className="slide-organizer__reset-btn"
						onClick={onReset}
					>
						Reset layout
					</button>
				)}
			</div>

			<div className="slide-organizer__grid">
				{slides.map((slide, index) => (
					<SlideCard
						key={`${slide.type}-${index}`}
						slide={slide}
						slideIndex={index}
						isDropTarget={dropTarget === index}
						isDragSource={dragState?.fromSlideIndex === index}
						playNameById={playNameById}
						onDragStart={handleDragStart}
						onDragOver={handleDragOver}
						onDragLeave={handleDragLeave}
						onDrop={handleDrop}
						onDragEnd={handleDragEnd}
					/>
				))}
			</div>

			{slides.length > 0 && (
				<p className="slide-organizer__hint">
					Drag plays between slides to customize layout
				</p>
			)}
		</div>
	)
}

// ===== Internal Types =====

interface DragState {
	blockId: string
	fromSlideIndex: number
}

// ===== SlideCard =====

interface SlideCardProps {
	slide: ExportSlide
	slideIndex: number
	isDropTarget: boolean
	isDragSource: boolean
	playNameById: Map<string, string>
	onDragStart: (blockId: string, slideIndex: number) => void
	onDragOver: (e: React.DragEvent, slideIndex: number) => void
	onDragLeave: () => void
	onDrop: (e: React.DragEvent, slideIndex: number) => void
	onDragEnd: () => void
}

function SlideCard({
	slide,
	slideIndex,
	isDropTarget,
	isDragSource,
	playNameById,
	onDragStart,
	onDragOver,
	onDragLeave,
	onDrop,
	onDragEnd,
}: SlideCardProps) {
	if (slide.type === 'divider') {
		return (
			<div className="slide-organizer__card" data-type="divider">
				<div className="slide-organizer__divider-content">
					<span className="slide-organizer__divider-title">{slide.title}</span>
				</div>
			</div>
		)
	}

	if (slide.type !== 'content') return null
	const blockIds = slide.blockIds

	return (
		<div
			className="slide-organizer__card"
			data-drop-target={isDropTarget}
			data-drag-source={isDragSource}
			onDragOver={(e) => onDragOver(e, slideIndex)}
			onDragLeave={onDragLeave}
			onDrop={(e) => onDrop(e, slideIndex)}
		>
			<div className="slide-organizer__card-header">
				<span className="slide-organizer__slide-number">
					Slide {slideIndex + 1}
				</span>
				<span className="slide-organizer__play-count">
					{blockIds.length} {blockIds.length === 1 ? 'play' : 'plays'}
				</span>
			</div>

			<div className="slide-organizer__plays" data-count={blockIds.length}>
				{blockIds.map((blockId) => (
					<div
						key={blockId}
						className="slide-organizer__play-item"
						draggable
						onDragStart={(e) => {
							e.dataTransfer.effectAllowed = 'move'
							e.dataTransfer.setData('text/plain', blockId)
							onDragStart(blockId, slideIndex)
						}}
						onDragEnd={onDragEnd}
					>
						<div className="slide-organizer__play-thumb">
							<CanvasThumbnail blockId={blockId} />
						</div>
						<span className="slide-organizer__play-name">
							{playNameById.get(blockId) ?? 'Play'}
						</span>
					</div>
				))}
			</div>

			{isDropTarget && (
				<div className="slide-organizer__drop-indicator">
					Drop here ({blockIds.length + 1} plays)
				</div>
			)}
		</div>
	)
}
